import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { DICT, SUPPORTED_LOCALES, DEFAULT_LOCALE, detectLocale, setLocale as persist } from "@/i18n/dict";

const LanguageContext = createContext({
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    t: (k) => k,
    locales: SUPPORTED_LOCALES,
});

export function LanguageProvider({ children }) {
    const [locale, setLocaleState] = useState(DEFAULT_LOCALE);

    // Detect on mount — client-side only
    useEffect(() => {
        const detected = detectLocale();
        setLocaleState(detected);
    }, []);

    // Sync <html lang> for accessibility + SEO
    useEffect(() => {
        try {
            document.documentElement.lang = locale === "chk" ? "chk" : "en";
        } catch { /* ignore */ }
    }, [locale]);

    const setLocale = useCallback((code) => {
        setLocaleState(code);
        persist(code);
    }, []);

    const t = useCallback((key) => {
        const row = DICT[key];
        if (!row) return key;
        return row[locale] || row.en || key;
    }, [locale]);

    const value = useMemo(() => ({
        locale,
        setLocale,
        t,
        locales: SUPPORTED_LOCALES,
    }), [locale, setLocale, t]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
    return useContext(LanguageContext);
}
